import Decimal from 'decimal.js';
const D = Decimal.clone({ precision: 80, rounding: Decimal.ROUND_HALF_UP });
function decimal(v: string | null): Decimal | null {
  try {
    if (v === null || !/^\d+(\.\d+)?$/.test(v)) return null;
    const n = new D(v);
    return n.isFinite() ? n : null;
  } catch {
    return null;
  }
}
export interface DevelopmentLine {
  inci: string;
  percent: string;
  activeFraction: string;
  supplierPricePerKg: string | null;
  priceClass: string;
  priceSource: string;
  priceDate: string;
}
export interface DevelopmentCharge {
  label: string;
  amountTHB: string | null;
  basis: 'per_unit' | 'per_batch';
  source: string;
  date: string;
  provenance: 'unknown' | 'assumption' | 'historical' | 'quote';
}
export function evaluateDevelopmentFormula(lines: readonly DevelopmentLine[]) {
  const issues: string[] = [];
  const values = lines.map((l) => ({ l, p: decimal(l.percent), a: decimal(l.activeFraction) }));
  if (values.some((v) => v.p === null || v.a === null || v.p.gt(100) || v.a.gt(1)))
    issues.push('invalid_active_or_percent');
  const total = values.reduce((s, v) => s.plus(v.p ?? 0), new D(0));
  if (!lines.length || !total.eq(100)) issues.push('formula_total_not_100');
  if (new Set(lines.map((l) => l.inci.trim().toLowerCase())).size !== lines.length)
    issues.push('duplicate_inci');
  return {
    total: total.toFixed(6),
    balance: new D(100).minus(total).toFixed(6),
    issues,
    active: values.map((v) => ({
      inci: v.l.inci,
      percent: v.p !== null && v.a !== null ? v.p.times(v.a).toFixed(6) : null,
    })),
    rounding: 'ROUND_HALF_UP; 6 decimal places for display; no automatic ingredient adjustment',
  };
}
export function indicativeDevelopmentCost(
  lines: readonly DevelopmentLine[],
  fillSize: string | null,
  unit: 'g' | 'mL',
  quantity: string | null,
  density: string | null,
  yieldFraction: string | null,
  charges: readonly DevelopmentCharge[] = [],
) {
  const fill = decimal(fillSize),
    units = decimal(quantity),
    rho = decimal(density),
    usableYield = decimal(yieldFraction);
  const issues: string[] = [];
  if (fill === null || units === null || fill.lte(0) || units.lte(0))
    issues.push('fill_or_quantity_unknown');
  if (unit === 'mL' && (rho === null || rho.lte(0))) issues.push('density_required');
  if (!['g', 'mL'].includes(unit)) issues.push('unit_invalid');
  if (usableYield === null || usableYield.lte(0) || usableYield.gt(1))
    issues.push('yield_required');
  issues.push(...evaluateDevelopmentFormula(lines).issues);
  const missing = lines
    .filter(
      (l) =>
        decimal(l.supplierPricePerKg) === null ||
        l.priceClass === 'unknown' ||
        !l.priceSource.trim() ||
        !/^\d{4}-\d{2}-\d{2}$/.test(l.priceDate),
    )
    .map((l) => l.inci);
  const mass =
    issues.length || fill === null || units === null || usableYield === null
      ? null
      : fill
          .times(units)
          .times(unit === 'mL' ? (rho ?? 0) : 1)
          .div(1000)
          .div(usableYield);
  const raw =
    mass === null
      ? null
      : lines
          .filter((l) => !missing.includes(l.inci))
          .reduce(
            (s, l) =>
              s.plus(
                mass
                  .times(l.percent)
                  .div(100)
                  .times(l.supplierPricePerKg ?? 0),
              ),
            new D(0),
          );
  const completeCharge = (c: DevelopmentCharge) =>
    decimal(c.amountTHB) !== null &&
    c.provenance !== 'unknown' &&
    Boolean(c.source.trim()) &&
    /^\d{4}-\d{2}-\d{2}$/.test(c.date);
  const missingCharges = charges.filter((c) => !completeCharge(c)).map((c) => c.label);
  const extra =
    units === null
      ? null
      : charges
          .filter(completeCharge)
          .reduce(
            (s, c) => s.plus(new D(c.amountTHB ?? 0).times(c.basis === 'per_unit' ? units : 1)),
            new D(0),
          );
  const known = raw !== null && extra !== null ? raw.plus(extra) : null;
  return {
    status: issues.length || missing.length || missingCharges.length ? 'incomplete' : 'indicative',
    massKg: mass?.toFixed(6) ?? null,
    knownRawMaterialSubtotalTHB: raw?.toFixed(2) ?? null,
    knownOtherCostsTHB: extra?.toFixed(2) ?? null,
    knownSubtotalTHB: known?.toFixed(2) ?? null,
    knownPerUnitTHB: known !== null && units?.gt(0) ? known.div(units).toFixed(4) : null,
    missingPrices: missing,
    missingCharges,
    issues,
    assumptions: {
      fillSize,
      unit,
      quantity,
      densityGml: density,
      usableYield: yieldFraction,
      concentrationBasis: 'as-supplied weight %; active % = dose × active fraction',
    },
    exclusions: ['tax', 'margin'],
    finalQuote: false,
  };
}
export function shiftDevelopmentPercent(percent: string, delta: string) {
  return new D(percent).plus(delta).toString();
}
